/**
 * mm-360-viewer.js
 * Equirectangular look-around for MapShare 360 stills.
 * Idempotent: each [data-mm-360] root inits once. Drag, touch, arrows, wheel.
 */
(function () {
  var DEG = Math.PI / 180;
  var VERTEX =
    'attribute vec3 aPos;' +
    'attribute vec2 aUv;' +
    'uniform mat4 uView;' +
    'uniform mat4 uProj;' +
    'varying vec2 vUv;' +
    'void main(){' +
    '  vUv = vec2(1.0 - aUv.x, 1.0 - aUv.y);' +
    '  gl_Position = uProj * uView * vec4(aPos, 1.0);' +
    '}';
  var FRAGMENT =
    'precision mediump float;' +
    'varying vec2 vUv;' +
    'uniform sampler2D uTex;' +
    'void main(){ gl_FragColor = texture2D(uTex, vUv); }';

  function clamp(n, lo, hi) {
    return Math.max(lo, Math.min(hi, n));
  }

  function compile(gl, type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function program(gl) {
    var vs = compile(gl, gl.VERTEX_SHADER, VERTEX);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    if (!vs || !fs) return null;
    var p = gl.createProgram();
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) return null;
    return p;
  }

  function sphere(latBands, lonBands) {
    var pos = [];
    var uv = [];
    var idx = [];
    var lat;
    var lon;
    for (lat = 0; lat <= latBands; lat++) {
      var theta = (lat * Math.PI) / latBands;
      var sinT = Math.sin(theta);
      var cosT = Math.cos(theta);
      for (lon = 0; lon <= lonBands; lon++) {
        var phi = (lon * 2 * Math.PI) / lonBands;
        pos.push(-Math.sin(phi) * sinT, cosT, Math.cos(phi) * sinT);
        uv.push(lon / lonBands, 1 - lat / latBands);
      }
    }
    for (lat = 0; lat < latBands; lat++) {
      for (lon = 0; lon < lonBands; lon++) {
        var first = lat * (lonBands + 1) + lon;
        var second = first + lonBands + 1;
        idx.push(first, second, first + 1);
        idx.push(second, second + 1, first + 1);
      }
    }
    return {
      pos: new Float32Array(pos),
      uv: new Float32Array(uv),
      idx: new Uint16Array(idx)
    };
  }

  function mat4Identity() {
    return new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  }

  function perspective(out, fovY, aspect, near, far) {
    var f = 1 / Math.tan(fovY / 2);
    var nf = 1 / (near - far);
    out[0] = f / aspect;
    out[1] = 0;
    out[2] = 0;
    out[3] = 0;
    out[4] = 0;
    out[5] = f;
    out[6] = 0;
    out[7] = 0;
    out[8] = 0;
    out[9] = 0;
    out[10] = (far + near) * nf;
    out[11] = -1;
    out[12] = 0;
    out[13] = 0;
    out[14] = 2 * far * near * nf;
    out[15] = 0;
    return out;
  }

  function viewYawPitch(out, yaw, pitch) {
    var cy = Math.cos(yaw);
    var sy = Math.sin(yaw);
    var cp = Math.cos(pitch);
    var sp = Math.sin(pitch);
    out[0] = cy;
    out[1] = sp * sy;
    out[2] = -cp * sy;
    out[3] = 0;
    out[4] = 0;
    out[5] = cp;
    out[6] = sp;
    out[7] = 0;
    out[8] = sy;
    out[9] = -sp * cy;
    out[10] = cp * cy;
    out[11] = 0;
    out[12] = 0;
    out[13] = 0;
    out[14] = 0;
    out[15] = 1;
    return out;
  }

  function bindBuffer(gl, type, data) {
    var buf = gl.createBuffer();
    gl.bindBuffer(type, buf);
    gl.bufferData(type, data, gl.STATIC_DRAW);
    return buf;
  }

  function loadTexture(gl, url, onReady) {
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      1,
      1,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      new Uint8Array([20, 22, 28, 255])
    );
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        onReady(true);
      } catch (err) {
        onReady(false);
      }
    };
    img.onerror = function () {
      onReady(false);
    };
    img.src = url;
    return tex;
  }

  function fallbackPanorama(root, url) {
    var stage = root.querySelector('[data-mm-360-stage]');
    if (!stage) return;
    stage.classList.add('is-fallback');
    var media = stage.querySelector('.mm-360__media') || stage;
    media.style.backgroundImage = 'url("' + url.replace(/"/g, '\\"') + '")';
    var x = 50;
    var dragging = false;
    var lastX = 0;
    function pos(clientX) {
      x = clamp(x - (clientX - lastX) * 0.08, 0, 100);
      lastX = clientX;
      media.style.backgroundPosition = x + '% 50%';
    }
    stage.addEventListener('pointerdown', function (e) {
      dragging = true;
      lastX = e.clientX;
      stage.setPointerCapture(e.pointerId);
      root.classList.add('is-dragging');
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      pos(e.clientX);
    });
    function up() {
      dragging = false;
      root.classList.remove('is-dragging');
    }
    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', up);
  }

  function initGl(root, url) {
    var canvas = root.querySelector('[data-mm-360-canvas]');
    if (!canvas) return false;
    var gl =
      canvas.getContext('webgl', { antialias: true, alpha: false }) ||
      canvas.getContext('experimental-webgl', { antialias: true, alpha: false });
    if (!gl) return false;
    var prog = program(gl);
    if (!prog) return false;

    var mesh = sphere(32, 64);
    var posBuf = bindBuffer(gl, gl.ARRAY_BUFFER, mesh.pos);
    var uvBuf = bindBuffer(gl, gl.ARRAY_BUFFER, mesh.uv);
    var idxBuf = bindBuffer(gl, gl.ELEMENT_ARRAY_BUFFER, mesh.idx);
    var aPos = gl.getAttribLocation(prog, 'aPos');
    var aUv = gl.getAttribLocation(prog, 'aUv');
    var uView = gl.getUniformLocation(prog, 'uView');
    var uProj = gl.getUniformLocation(prog, 'uProj');
    var view = mat4Identity();
    var proj = mat4Identity();
    var yaw = 0;
    var pitch = 0;
    var fov = 75 * DEG;
    var dragging = false;
    var lastX = 0;
    var lastY = 0;
    var dirty = true;
    var ready = false;

    loadTexture(gl, url, function (ok) {
      if (!ok) {
        fallbackPanorama(root, url);
        return;
      }
      ready = true;
      dirty = true;
      draw();
    });

    function size() {
      var wrap = canvas.parentElement;
      var w = wrap ? wrap.clientWidth : canvas.clientWidth;
      var h = wrap ? wrap.clientHeight : canvas.clientHeight;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var pw = Math.max(1, Math.floor(w * dpr));
      var ph = Math.max(1, Math.floor(h * dpr));
      if (canvas.width !== pw || canvas.height !== ph) {
        canvas.width = pw;
        canvas.height = ph;
        gl.viewport(0, 0, pw, ph);
        dirty = true;
      }
      perspective(proj, fov, pw / ph, 0.1, 10);
    }

    function draw() {
      if (!ready) return;
      size();
      if (!dirty) return;
      dirty = false;
      viewYawPitch(view, yaw, pitch);
      gl.clearColor(0.05, 0.06, 0.08, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(prog);
      gl.uniformMatrix4fv(uView, false, view);
      gl.uniformMatrix4fv(uProj, false, proj);
      gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, uvBuf);
      gl.enableVertexAttribArray(aUv);
      gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, idxBuf);
      gl.drawElements(gl.TRIANGLES, mesh.idx.length, gl.UNSIGNED_SHORT, 0);
    }

    function hideHint() {
      root.classList.add('is-looked');
    }

    canvas.addEventListener('pointerdown', function (e) {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
      root.classList.add('is-dragging');
      hideHint();
    });
    canvas.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - lastX;
      var dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      yaw -= dx * 0.005;
      pitch = clamp(pitch - dy * 0.005, -80 * DEG, 80 * DEG);
      dirty = true;
      draw();
    });
    function up() {
      dragging = false;
      root.classList.remove('is-dragging');
    }
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    canvas.addEventListener(
      'wheel',
      function (e) {
        e.preventDefault();
        fov = clamp(fov + (e.deltaY > 0 ? 4 : -4) * DEG, 40 * DEG, 100 * DEG);
        dirty = true;
        draw();
      },
      { passive: false }
    );
    canvas.addEventListener('keydown', function (e) {
      var step = 4 * DEG;
      if (e.key === 'ArrowLeft') yaw += step;
      else if (e.key === 'ArrowRight') yaw -= step;
      else if (e.key === 'ArrowUp') pitch = clamp(pitch + step, -80 * DEG, 80 * DEG);
      else if (e.key === 'ArrowDown') pitch = clamp(pitch - step, -80 * DEG, 80 * DEG);
      else if (e.key === '+' || e.key === '=') fov = clamp(fov - 4 * DEG, 40 * DEG, 100 * DEG);
      else if (e.key === '-' || e.key === '_') fov = clamp(fov + 4 * DEG, 40 * DEG, 100 * DEG);
      else return;
      e.preventDefault();
      hideHint();
      dirty = true;
      draw();
    });

    var fsBtn = root.querySelector('[data-mm-360-fs]');
    if (fsBtn) {
      fsBtn.addEventListener('click', function () {
        var node = root.querySelector('[data-mm-360-stage]');
        if (!node) return;
        if (!document.fullscreenElement) {
          if (node.requestFullscreen) node.requestFullscreen();
        } else if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      });
    }

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () {
        dirty = true;
        draw();
      });
      ro.observe(canvas.parentElement || canvas);
    } else {
      window.addEventListener('resize', function () {
        dirty = true;
        draw();
      });
    }

    draw();
    return true;
  }

  function wireMapshare(root) {
    var mapBtn = root.querySelector('.mm-360__mapshare');
    if (!mapBtn || mapBtn.getAttribute('data-mm-360-wired') === '1') return;
    mapBtn.setAttribute('data-mm-360-wired', '1');
    var openMap = function () {
      mapBtn.classList.add('is-open');
    };
    var closeMap = function () {
      mapBtn.classList.remove('is-open');
    };
    mapBtn.addEventListener('mouseenter', openMap);
    mapBtn.addEventListener('mouseleave', closeMap);
    mapBtn.addEventListener('focus', openMap);
    mapBtn.addEventListener('blur', closeMap);
  }

  function initRoot(root) {
    if (root.getAttribute('data-mm-360-ready') === '1') return;
    var url = root.getAttribute('data-src');
    if (!url) return;
    root.setAttribute('data-mm-360-ready', '1');
    wireMapshare(root);
    if (!initGl(root, url)) fallbackPanorama(root, url);
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-mm-360]');
    if (!nodes.length) return;
    if (typeof IntersectionObserver === 'undefined') {
      nodes.forEach(initRoot);
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          initRoot(entry.target);
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '200px' }
    );
    nodes.forEach(function (node) {
      io.observe(node);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
